import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { TID } from '@/constants/testIds/voice';
import { ArrowRight } from '@phosphor-icons/react';

const Login = () => {
    const { login, user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [totpCode, setTotpCode] = useState('');
    const [needs2fa, setNeeds2fa] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (user) navigate('/', { replace: true });
    }, [user, navigate]);

    const onSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setLoading(true);
        try {
            await login(email, password, needs2fa ? totpCode : undefined);
            const dest = location.state?.from?.pathname || '/';
            navigate(dest, { replace: true });
        } catch (err) {
            const detail = err.response?.data?.detail;
            const status = err.response?.status;
            if (status === 428 && detail === '2fa_required') {
                setNeeds2fa(true);
                setError(null);
            } else if (detail === '2fa_invalid') {
                setError('Neplatný 2FA kód');
            } else {
                setError(detail || 'Přihlášení se nezdařilo');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center"
             style={{ background: 'var(--bg-app)' }}>
            <form
                onSubmit={onSubmit}
                className="w-full max-w-sm border p-8 mx-4"
                style={{
                    background: 'var(--bg-surface)',
                    borderColor: 'var(--border-default)',
                    borderRadius: 2,
                }}
            >
                <div className="flex flex-col items-center mb-6">
                    <img src="/logo.png" alt="Solar Green" className="h-16 mb-4" />
                    <h1 className="font-heading text-lg font-bold uppercase tracking-[0.15em]"
                        style={{ color: 'var(--text-primary)' }}>
                        VOIP Gateway
                    </h1>
                    <div className="text-[10px] uppercase tracking-[0.2em] mt-1"
                         style={{ color: 'var(--text-disabled)' }}>
                        Privátní server
                    </div>
                </div>

                <div className="mt-6">
                    <label className="text-[10px] uppercase tracking-[0.15em] font-bold"
                           style={{ color: 'var(--text-secondary)' }}>Jméno</label>
                    <input
                        data-testid={TID.loginEmail}
                        type="text"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        autoComplete="username"
                        required
                        className="w-full mt-2 px-3 py-2 bg-[var(--bg-app)] border border-[var(--border-default)] text-sm font-mono focus:outline-none focus:border-[var(--brand)]"
                        style={{ borderRadius: 2 }}
                    />
                </div>

                <div className="mt-4">
                    <label className="text-[10px] uppercase tracking-[0.15em] font-bold"
                           style={{ color: 'var(--text-secondary)' }}>Heslo</label>
                    <input
                        data-testid={TID.loginPassword}
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        autoComplete="current-password"
                        required
                        className="w-full mt-2 px-3 py-2 bg-[var(--bg-app)] border border-[var(--border-default)] text-sm font-mono focus:outline-none focus:border-[var(--brand)]"
                        style={{ borderRadius: 2 }}
                    />
                </div>

                {needs2fa && (
                    <div className="mt-4">
                        <label className="text-[10px] uppercase tracking-[0.15em] font-bold"
                               style={{ color: 'var(--brand)' }}>
                            2FA kód (Google Authenticator)
                        </label>
                        <input
                            data-testid="login-totp-input"
                            type="text"
                            value={totpCode}
                            onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                            placeholder="123456"
                            autoFocus
                            className="w-full mt-2 px-3 py-2 bg-[var(--bg-app)] border text-2xl font-mono text-center tracking-[0.3em] focus:outline-none focus:border-[var(--brand)]"
                            style={{ borderRadius: 2, borderColor: 'var(--brand)' }}
                        />
                        <div className="text-[10px] mt-1" style={{ color: 'var(--text-disabled)' }}>
                            Nebo zadejte recovery kód, pokud nemáte přístup k telefonu.
                        </div>
                    </div>
                )}

                {error && (
                    <div className="mt-4 px-3 py-2 text-xs"
                         style={{ background: 'rgba(239,68,68,0.1)', color: '#FCA5A5', borderRadius: 2 }}>
                        {error}
                    </div>
                )}

                <button
                    data-testid={TID.loginSubmit}
                    type="submit"
                    disabled={loading}
                    className="w-full mt-6 py-2.5 text-sm font-bold uppercase tracking-[0.1em] flex items-center justify-center gap-2"
                    style={{
                        background: loading ? 'var(--brand-active)' : 'var(--brand)',
                        color: '#fff',
                        borderRadius: 2,
                    }}
                >
                    {loading ? 'Přihlašuji...' : 'Vstoupit'}
                    {!loading && <ArrowRight size={16} weight="bold" />}
                </button>

                <div className="mt-6 pt-4 border-t text-center text-[10px] uppercase tracking-[0.15em]"
                     style={{ borderColor: 'var(--border-default)', color: 'var(--text-disabled)' }}>
                    © Solar Green · Private System
                </div>
            </form>
        </div>
    );
};

export default Login;
